import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    alpha,
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Snackbar,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import LinkIcon from "@mui/icons-material/Link";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import {
    createModule,
    createModuleSection,
    deleteModuleSection,
    deleteModule,
    getAuthorsAdminBySubject,
    getModuleSectionsAdmin,
    getModulesAdminByAuthor,
    getSubjectsAdminByClass,
    patchModuleSection,
    patchModule,
    setModuleEnabled,
} from "../../api/subjects";

import ModuleQuestionsEditorDialog from "./ModuleQuestionsEditorDialog";

const SECTION_ICON_OPTIONS = [
    { value: "test", label: "Тестові завдання" },
    { value: "interactive", label: "Інтерактивні вправи" },
    { value: "video", label: "Відео / самостійна робота" },
    { value: "openBook", label: "Розгорнута книжка" },
    { value: "school", label: "Школа / урок" },
    { value: "reading", label: "Читання / бібліотека" },
    { value: "math", label: "Математика" },
    { value: "science", label: "Наука / досліди" },
    { value: "creative", label: "Творче завдання" },
    { value: "puzzle", label: "Логіка / пазл" },
    { value: "checklist", label: "Перевірка знань" },
    { value: "audio", label: "Аудіо / слухання" },
];

const normalize = (value) => String(value ?? "").trim();
const slugifySection = (value) =>
    normalize(value)
        .toLowerCase()
        .replace(/[^a-z0-9а-яіїєґ]+/gi, "-")
        .replace(/^-+|-+$/g, "");
const normalizeSection = (section) => {
    const title = normalize(section?.title);
    const sectionSlug = normalize(section?.sectionSlug || section?.slug) || slugifySection(title);

    return {
        ...section,
        sectionSlug,
        title,
        description: normalize(section?.description),
        iconKey: normalize(section?.iconKey) || "test",
        order: Number(section?.order ?? 0) || 0,
        enabled: section?.enabled !== false,
    };
};

const mergeSections = (moduleSections, catalogSections) => {
    const map = new Map();

    [...(Array.isArray(moduleSections) ? moduleSections : []), ...(Array.isArray(catalogSections) ? catalogSections : [])]
        .map(normalizeSection)
        .filter((section) => section.sectionSlug)
        .forEach((section) => {
            map.set(section.sectionSlug, section);
        });

    return Array.from(map.values()).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
};

const getModuleQuestionStats = (module) => {
    const questions = Array.isArray(module?.questions) ? module.questions : [];
    const usesSections = questions.some((question) => normalize(question?.sectionSlug));
    const questionsWithoutSection = usesSections
        ? questions
            .map((question, index) => ({ question, index }))
            .filter(({ question }) => !normalize(question?.sectionSlug))
        : [];

    return {
        count: questions.length,
        questionsWithoutSection,
    };
};

const getQuestionId = (question) => normalize(question?.id);
const getQuestionSectionSlug = (question) => normalize(question?.sectionSlug);
const getSectionSlug = (section) => normalize(section?.sectionSlug || section?.slug);

const buildEmbedBaseUrl = (classId, subjectSlug, authorSlug, moduleSlug) => {
    const origin = window.location?.origin || "";
    return (
        `${origin}/embed/class/${encodeURIComponent(classId)}` +
        `/${encodeURIComponent(subjectSlug)}` +
        `/${encodeURIComponent(authorSlug)}` +
        `/modules/${encodeURIComponent(moduleSlug)}`
    );
};

const buildQuestionEmbedUrl = ({ classId, subjectSlug, authorSlug, moduleSlug, question, index }) => {
    const params = new URLSearchParams();
    const questionId = getQuestionId(question);

    if (questionId) {
        params.set("q", questionId);
    } else {
        params.set("index", String(index));
    }

    return `${buildEmbedBaseUrl(classId, subjectSlug, authorSlug, moduleSlug)}?${params.toString()}`;
};

const buildIframeCode = (url) =>
    `<iframe src="${url}" width="100%" height="700" style="border:0;" loading="lazy"></iframe>`;

const buildEmbedEntries = (module, availableSections, classId, subjectSlug, authorSlug) => {
    const questions = Array.isArray(module?.questions) ? module.questions : [];
    const moduleSlug = normalize(module?.moduleSlug);
    if (!moduleSlug || questions.length === 0) return [];

    const questionSectionSlugs = Array.from(new Set(questions.map(getQuestionSectionSlug).filter(Boolean)));
    const questionSections = new Set(questionSectionSlugs);
    const mergedSections = mergeSections(module?.sections || [], availableSections)
        .filter((section) => section.enabled !== false && questionSections.has(getSectionSlug(section)));
    const knownSectionSlugs = new Set(mergedSections.map(getSectionSlug));
    const unknownSections = questionSectionSlugs
        .filter((sectionSlug) => !knownSectionSlugs.has(sectionSlug))
        .map((sectionSlug) => ({ sectionSlug, title: sectionSlug, enabled: true }));
    const sectionsForEmbed = [...mergedSections, ...unknownSections];

    if (sectionsForEmbed.length > 0) {
        return sectionsForEmbed
            .map((section) => {
                const sectionSlug = getSectionSlug(section);
                const index = questions.findIndex((question) => getQuestionSectionSlug(question) === sectionSlug);
                const question = index >= 0 ? questions[index] : null;
                if (!question) return null;

                const url = buildQuestionEmbedUrl({
                    classId,
                    subjectSlug,
                    authorSlug,
                    moduleSlug,
                    question,
                    index,
                });

                return {
                    key: sectionSlug,
                    title: section.title || sectionSlug,
                    subtitle: `Перше завдання секції, #${index + 1}`,
                    url,
                    iframe: buildIframeCode(url),
                };
            })
            .filter(Boolean);
    }

    const url = buildQuestionEmbedUrl({
        classId,
        subjectSlug,
        authorSlug,
        moduleSlug,
        question: questions[0],
        index: 0,
    });

    return [
        {
            key: "module",
            title: module?.title || moduleSlug,
            subtitle: "Перше завдання модуля",
            url,
            iframe: buildIframeCode(url),
        },
    ];
};

const ModulesAdminPanel = ({ classId, subjectSlug, authorSlug, onBack }) => {
    const [modules, setModules] = useState([]);
    const [sections, setSections] = useState([]);
    const [loading, setLoading] = useState(false);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [moduleSlug, setModuleSlug] = useState("");
    const [title, setTitle] = useState("");
    const [order, setOrder] = useState(0);
    const [pdfPath, setPdfPath] = useState("");
    const [pdfFileName, setPdfFileName] = useState("");
    const [creating, setCreating] = useState(false);

    const [deletingSlug, setDeletingSlug] = useState("");
    const [deleteDialogModule, setDeleteDialogModule] = useState(null);
    const [deleteConfirmText, setDeleteConfirmText] = useState("");

    const [sectionsDialogOpen, setSectionsDialogOpen] = useState(false);
    const [newSectionTitle, setNewSectionTitle] = useState("");
    const [newSectionDescription, setNewSectionDescription] = useState("");
    const [newSectionIconKey, setNewSectionIconKey] = useState("test");
    const [newSectionOrder, setNewSectionOrder] = useState(0);
    const [creatingSection, setCreatingSection] = useState(false);
    const [savingSectionSlug, setSavingSectionSlug] = useState("");
    const [deletingSectionSlug, setDeletingSectionSlug] = useState("");

    const [editorOpen, setEditorOpen] = useState(false);
    const [editingModule, setEditingModule] = useState(null);
    const [savingQuestions, setSavingQuestions] = useState(false);
    const [embedDialogModule, setEmbedDialogModule] = useState(null);
    const [copySourceModule, setCopySourceModule] = useState(null);
    const [copyTargetClassId, setCopyTargetClassId] = useState("");
    const [copyTargetSubjectSlug, setCopyTargetSubjectSlug] = useState("");
    const [copyTargetAuthorSlug, setCopyTargetAuthorSlug] = useState("");
    const [copyTargetSlug, setCopyTargetSlug] = useState("");
    const [copyTargetSubjects, setCopyTargetSubjects] = useState([]);
    const [copyTargetAuthors, setCopyTargetAuthors] = useState([]);
    const [copyTargetModules, setCopyTargetModules] = useState([]);
    const [copyTargetsLoading, setCopyTargetsLoading] = useState(false);
    const [copyingQuestions, setCopyingQuestions] = useState(false);

    const [snack, setSnack] = useState({
        open: false,
        severity: "success",
        message: "",
    });

    const headerSubtitle = useMemo(() => {
        const parts = [];
        if (classId) parts.push(`Клас: ${classId}`);
        if (subjectSlug) parts.push(`Предмет: ${subjectSlug}`);
        if (authorSlug) parts.push(`Автор: ${authorSlug}`);
        return parts.join(" • ");
    }, [classId, subjectSlug, authorSlug]);

    const availableSections = useMemo(
        () => (Array.isArray(sections) ? sections.map(normalizeSection).filter((section) => section.sectionSlug) : []),
        [sections]
    );

    const questionSummary = useMemo(() => {
        const byModuleSlug = new Map();
        let total = 0;
        let totalWithoutSection = 0;

        modules.forEach((module) => {
            const stats = getModuleQuestionStats(module);
            byModuleSlug.set(module.moduleSlug, stats);
            total += stats.count;
            totalWithoutSection += stats.questionsWithoutSection.length;
        });

        return {
            byModuleSlug,
            total,
            totalWithoutSection,
            modulesWithMissingSections: modules.filter(
                (module) => (byModuleSlug.get(module.moduleSlug)?.questionsWithoutSection.length || 0) > 0
            ),
        };
    }, [modules]);

    const embedEntries = useMemo(
        () => buildEmbedEntries(embedDialogModule, availableSections, classId, subjectSlug, authorSlug),
        [embedDialogModule, availableSections, classId, subjectSlug, authorSlug]
    );

    const emptyCopyTargets = useMemo(() => {
        const targetsCurrentAuthor =
            copyTargetClassId === classId &&
            copyTargetSubjectSlug === subjectSlug &&
            copyTargetAuthorSlug === authorSlug;

        return copyTargetModules.filter(
            (module) =>
                (!targetsCurrentAuthor || module.moduleSlug !== copySourceModule?.moduleSlug) &&
                (!Array.isArray(module.questions) || module.questions.length === 0)
        );
    }, [
        copyTargetModules,
        copyTargetClassId,
        copyTargetSubjectSlug,
        copyTargetAuthorSlug,
        classId,
        subjectSlug,
        authorSlug,
        copySourceModule,
    ]);

    const loadModules = useCallback(
        async (opts) => {
            const silent = Boolean(opts && opts.silent);
            if (!classId || !subjectSlug || !authorSlug) return;

            if (!silent) setLoading(true);
            try {
                const data = await getModulesAdminByAuthor(classId, subjectSlug, authorSlug);
                setModules(Array.isArray(data) ? data : []);
            } catch (e) {
                const msg =
                    e?.response?.data ||
                    e?.message ||
                    "Не вдалося завантажити список модулів";
                setSnack({ open: true, severity: "error", message: String(msg) });
                setModules([]);
            } finally {
                if (!silent) setLoading(false);
            }
        },
        [classId, subjectSlug, authorSlug]
    );

    const loadSections = useCallback(
        async (opts) => {
            const silent = Boolean(opts && opts.silent);
            if (!classId || !subjectSlug || !authorSlug) return;

            try {
                const data = await getModuleSectionsAdmin(classId, subjectSlug, authorSlug);
                setSections(Array.isArray(data) ? data.map(normalizeSection).filter((section) => section.sectionSlug) : []);
            } catch (e) {
                setSections([]);
                if (!silent) {
                    const msg = e?.response?.data || e?.message || "Не вдалося завантажити секції";
                    setSnack({ open: true, severity: "error", message: String(msg) });
                }
            }
        },
        [classId, subjectSlug, authorSlug]
    );

    useEffect(() => {
        loadModules();
        loadSections({ silent: true });
    }, [loadModules, loadSections]);

    function openDialog() {
        setModuleSlug("");
        setTitle("");
        setOrder(0);
        setPdfPath("");
        setPdfFileName("");
        setDialogOpen(true);
    }

    function closeDialog() {
        if (!creating) setDialogOpen(false);
    }

    async function onCreate() {
        const ms = String(moduleSlug || "").trim();
        const t = String(title || "").trim();

        if (!ms) {
            setSnack({ open: true, severity: "error", message: "Введіть moduleSlug" });
            return;
        }
        if (!t) {
            setSnack({ open: true, severity: "error", message: "Введіть назву модуля" });
            return;
        }

        setCreating(true);
        try {
            await createModule(classId, subjectSlug, authorSlug, {
                moduleSlug: ms,
                title: t,
                order: Number(order) || 0,
                pdfPath: String(pdfPath || "").trim() || null,
                pdfFileName: String(pdfFileName || "").trim() || null,
                questions: [],
                sections: [],
            });

            setSnack({ open: true, severity: "success", message: "Модуль додано" });
            setDialogOpen(false);
            await loadModules({ silent: true });
        } catch (e) {
            const status = e?.response?.status;
            const msg = e?.response?.data || e?.message || "Не вдалося додати модуль";
            setSnack({
                open: true,
                severity: "error",
                message: status ? `${status}: ${msg}` : String(msg),
            });
        } finally {
            setCreating(false);
        }
    }

    async function onToggleEnabled(mSlug, next) {
        try {
            await setModuleEnabled(classId, subjectSlug, authorSlug, mSlug, next);
            setModules((prev) =>
                prev.map((x) => (x.moduleSlug === mSlug ? { ...x, enabled: next } : x))
            );
            setSnack({
                open: true,
                severity: "success",
                message: next ? "Модуль увімкнено" : "Модуль вимкнено",
            });
        } catch (e) {
            const msg = e?.response?.data || e?.message || "Не вдалося змінити статус";
            setSnack({ open: true, severity: "error", message: String(msg) });
        }
    }

    function openDeleteDialog(module) {
        if (!module?.moduleSlug) return;
        setDeleteDialogModule(module);
        setDeleteConfirmText("");
    }

    function closeDeleteDialog() {
        if (!deletingSlug) {
            setDeleteDialogModule(null);
            setDeleteConfirmText("");
        }
    }

    async function onDelete(mSlug) {
        if (!mSlug) return false;
        setDeletingSlug(mSlug);
        try {
            await deleteModule(classId, subjectSlug, authorSlug, mSlug);
            setModules((prev) => prev.filter((x) => x.moduleSlug !== mSlug));
            setSnack({ open: true, severity: "success", message: "Модуль видалено" });
            return true;
        } catch (e) {
            const msg = e?.response?.data || e?.message || "Не вдалося видалити модуль";
            setSnack({ open: true, severity: "error", message: String(msg) });
            return false;
        } finally {
            setDeletingSlug("");
        }
    }

    async function confirmDeleteModule() {
        const mSlug = deleteDialogModule?.moduleSlug;
        if (!mSlug || normalize(deleteConfirmText) !== "видалити") return;

        const deleted = await onDelete(mSlug);
        if (deleted) {
            setDeleteDialogModule(null);
            setDeleteConfirmText("");
        }
    }

    function openEditor(m) {
        setEditingModule(m);
        setEditorOpen(true);
    }

    function openEmbedDialog(module) {
        if (!module?.moduleSlug) return;
        setEmbedDialogModule(module);
    }

    async function loadCopySubjects(targetClassId) {
        if (!targetClassId) return;
        setCopyTargetsLoading(true);
        try {
            const data = await getSubjectsAdminByClass(targetClassId);
            setCopyTargetSubjects(Array.isArray(data) ? data : []);
        } catch (e) {
            const msg = e?.response?.data || e?.message || "Не вдалося завантажити предмети";
            setSnack({ open: true, severity: "error", message: String(msg) });
            setCopyTargetSubjects([]);
        } finally {
            setCopyTargetsLoading(false);
        }
    }

    async function onCopyTargetClassChange(nextClassId) {
        setCopyTargetClassId(nextClassId);
        setCopyTargetSubjectSlug("");
        setCopyTargetAuthorSlug("");
        setCopyTargetSlug("");
        setCopyTargetSubjects([]);
        setCopyTargetAuthors([]);
        setCopyTargetModules([]);
        await loadCopySubjects(nextClassId);
    }

    async function onCopyTargetSubjectChange(nextSubjectSlug) {
        setCopyTargetSubjectSlug(nextSubjectSlug);
        setCopyTargetAuthorSlug("");
        setCopyTargetSlug("");
        setCopyTargetAuthors([]);
        setCopyTargetModules([]);
        if (!copyTargetClassId || !nextSubjectSlug) return;

        setCopyTargetsLoading(true);
        try {
            const data = await getAuthorsAdminBySubject(copyTargetClassId, nextSubjectSlug);
            setCopyTargetAuthors(Array.isArray(data) ? data : []);
        } catch (e) {
            const msg = e?.response?.data || e?.message || "Не вдалося завантажити авторів";
            setSnack({ open: true, severity: "error", message: String(msg) });
            setCopyTargetAuthors([]);
        } finally {
            setCopyTargetsLoading(false);
        }
    }

    async function onCopyTargetAuthorChange(nextAuthorSlug) {
        setCopyTargetAuthorSlug(nextAuthorSlug);
        setCopyTargetSlug("");
        setCopyTargetModules([]);
        if (!copyTargetClassId || !copyTargetSubjectSlug || !nextAuthorSlug) return;

        setCopyTargetsLoading(true);
        try {
            const data = await getModulesAdminByAuthor(
                copyTargetClassId,
                copyTargetSubjectSlug,
                nextAuthorSlug
            );
            setCopyTargetModules(Array.isArray(data) ? data : []);
        } catch (e) {
            const msg = e?.response?.data || e?.message || "Не вдалося завантажити модулі";
            setSnack({ open: true, severity: "error", message: String(msg) });
            setCopyTargetModules([]);
        } finally {
            setCopyTargetsLoading(false);
        }
    }

    function openCopyDialog(module) {
        if (!module?.moduleSlug || !Array.isArray(module.questions) || module.questions.length === 0) return;
        setCopySourceModule(module);
        setCopyTargetClassId("");
        setCopyTargetSubjectSlug("");
        setCopyTargetAuthorSlug("");
        setCopyTargetSlug("");
        setCopyTargetSubjects([]);
        setCopyTargetAuthors([]);
        setCopyTargetModules([]);
    }

    function closeCopyDialog() {
        if (copyingQuestions) return;
        setCopySourceModule(null);
        setCopyTargetClassId("");
        setCopyTargetSubjectSlug("");
        setCopyTargetAuthorSlug("");
        setCopyTargetSlug("");
        setCopyTargetSubjects([]);
        setCopyTargetAuthors([]);
        setCopyTargetModules([]);
    }

    async function copyQuestionsToModule() {
        const sourceQuestions = copySourceModule?.questions;
        const targetModule = emptyCopyTargets.find((module) => module.moduleSlug === copyTargetSlug);
        if (!Array.isArray(sourceQuestions) || sourceQuestions.length === 0 || !targetModule) return;

        const copiedQuestions = JSON.parse(JSON.stringify(sourceQuestions));
        setCopyingQuestions(true);
        try {
            await patchModule(
                copyTargetClassId,
                copyTargetSubjectSlug,
                copyTargetAuthorSlug,
                targetModule.moduleSlug,
                { questions: copiedQuestions }
            );

            const targetsCurrentAuthor =
                copyTargetClassId === classId &&
                copyTargetSubjectSlug === subjectSlug &&
                copyTargetAuthorSlug === authorSlug;
            if (targetsCurrentAuthor) {
                setModules((prev) =>
                    prev.map((module) =>
                        module.moduleSlug === targetModule.moduleSlug
                            ? { ...module, questions: copiedQuestions }
                            : module
                    )
                );
            }
            setSnack({
                open: true,
                severity: "success",
                message:
                    `Скопійовано ${copiedQuestions.length} завдань: ` +
                    `${copyTargetClassId} клас / ${copyTargetSubjectSlug} / ` +
                    `${copyTargetAuthorSlug} / ${targetModule.moduleSlug}`,
            });
            setCopySourceModule(null);
            setCopyTargetClassId("");
            setCopyTargetSubjectSlug("");
            setCopyTargetAuthorSlug("");
            setCopyTargetSlug("");
            setCopyTargetSubjects([]);
            setCopyTargetAuthors([]);
            setCopyTargetModules([]);
        } catch (e) {
            const msg = e?.response?.data || e?.message || "Не вдалося скопіювати завдання";
            setSnack({ open: true, severity: "error", message: String(msg) });
        } finally {
            setCopyingQuestions(false);
        }
    }

    function closeEmbedDialog() {
        setEmbedDialogModule(null);
    }

    async function copyEmbedText(value) {
        try {
            await navigator.clipboard.writeText(value);
            setSnack({ open: true, severity: "success", message: "Скопійовано" });
        } catch (e) {
            setSnack({ open: true, severity: "warning", message: "Не вдалося скопіювати автоматично" });
        }
    }

    function resetNewSectionForm() {
        setNewSectionTitle("");
        setNewSectionDescription("");
        setNewSectionIconKey("test");
        setNewSectionOrder(0);
    }

    async function onCreateSection() {
        const titleValue = normalize(newSectionTitle);
        if (!titleValue) {
            setSnack({ open: true, severity: "error", message: "Введіть назву секції" });
            return;
        }

        setCreatingSection(true);
        try {
            await createModuleSection(classId, subjectSlug, authorSlug, {
                title: titleValue,
                description: normalize(newSectionDescription),
                iconKey: newSectionIconKey || "test",
                order: Number(newSectionOrder) || 0,
                enabled: true,
            });
            setSnack({ open: true, severity: "success", message: "Секцію додано" });
            resetNewSectionForm();
            await loadSections({ silent: true });
        } catch (e) {
            const msg = e?.response?.data || e?.message || "Не вдалося додати секцію";
            setSnack({ open: true, severity: "error", message: String(msg) });
        } finally {
            setCreatingSection(false);
        }
    }

    async function onPatchSection(section, patch) {
        const sectionSlug = section?.sectionSlug;
        if (!sectionSlug) return;

        setSavingSectionSlug(sectionSlug);
        try {
            await patchModuleSection(classId, subjectSlug, authorSlug, sectionSlug, patch);
            await loadSections({ silent: true });
            await loadModules({ silent: true });
            setSnack({ open: true, severity: "success", message: "Секцію оновлено" });
        } catch (e) {
            const msg = e?.response?.data || e?.message || "Не вдалося оновити секцію";
            setSnack({ open: true, severity: "error", message: String(msg) });
        } finally {
            setSavingSectionSlug("");
        }
    }

    async function onDeleteSection(sectionSlug) {
        if (!sectionSlug) return;

        setDeletingSectionSlug(sectionSlug);
        try {
            await deleteModuleSection(classId, subjectSlug, authorSlug, sectionSlug);
            await loadSections({ silent: true });
            setSnack({ open: true, severity: "success", message: "Секцію видалено" });
        } catch (e) {
            const msg = e?.response?.data || e?.message || "Не вдалося видалити секцію";
            setSnack({ open: true, severity: "error", message: String(msg) });
        } finally {
            setDeletingSectionSlug("");
        }
    }

    function closeEditor() {
        if (!savingQuestions) setEditorOpen(false);
    }

    async function onSaveQuestions(nextQuestions, nextSections) {
        if (!editingModule?.moduleSlug) return;
        setSavingQuestions(true);
        try {
            await patchModule(
                classId,
                subjectSlug,
                authorSlug,
                editingModule.moduleSlug,
                {
                    questions: nextQuestions,
                    sections: nextSections,
                }
            );

            setModules((prev) =>
                prev.map((x) =>
                    x.moduleSlug === editingModule.moduleSlug
                        ? { ...x, questions: nextQuestions, sections: nextSections }
                        : x
                )
            );

            setSnack({ open: true, severity: "success", message: "Контент збережено" });
            setEditorOpen(false);
        } catch (e) {
            const msg = e?.response?.data || e?.message || "Не вдалося зберегти контент";
            setSnack({ open: true, severity: "error", message: String(msg) });
        } finally {
            setSavingQuestions(false);
        }
    }

    return (
        <Container disableGutters maxWidth={false} sx={{ mt: 2 }}>
            <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ sm: "center" }}
                justifyContent="space-between"
                sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    bgcolor: "background.paper",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    px: 2,
                    py: 2,
                }}
            >
                <Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <IconButton onClick={onBack} size="small">
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h5" fontWeight={700}>
                            Модулі
                        </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                        {headerSubtitle}
                    </Typography>
                </Box>

                <Stack direction="row" spacing={2} alignItems="center">
                    <Button
                        variant="outlined"
                        onClick={() => setSectionsDialogOpen(true)}
                        disabled={!classId || !subjectSlug || !authorSlug}
                    >
                        Секції
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={openDialog}
                        disabled={!classId || !subjectSlug || !authorSlug}
                    >
                        Додати
                    </Button>
                </Stack>
            </Stack>

            <Card variant="outlined">
                <CardContent>
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                        alignItems={{ sm: "center" }}
                        justifyContent="space-between"
                        sx={{
                            position: "sticky",
                            top: 0,
                            zIndex: 2,
                            display: "none",
                            bgcolor: "background.paper",
                            borderBottom: "1px solid",
                            borderColor: "divider",
                            mx: -2,
                            px: 2,
                            py: 2,
                        }}
                    >
                        <Box>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <IconButton onClick={onBack} size="small">
                                    <ArrowBackIcon />
                                </IconButton>
                                <Typography variant="h5" fontWeight={700}>
                                    Модулі
                                </Typography>
                            </Stack>
                            <Typography variant="body2" color="text.secondary">
                                {headerSubtitle}
                            </Typography>
                        </Box>

                        <Stack direction="row" spacing={2} alignItems="center">
                            <Button
                                variant="outlined"
                                onClick={() => setSectionsDialogOpen(true)}
                                disabled={!classId || !subjectSlug || !authorSlug}
                            >
                                Секції
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={openDialog}
                                disabled={!classId || !subjectSlug || !authorSlug}
                            >
                                Додати
                            </Button>
                        </Stack>
                    </Stack>

                    <Box
                        sx={{
                            mt: 2,
                            mb: 2,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 1.5,
                            px: 2,
                            py: 1.5,
                            border: "1px solid",
                            borderColor: "primary.main",
                            borderRadius: 2,
                            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 40,
                                height: 40,
                                borderRadius: "50%",
                                color: "primary.contrastText",
                                bgcolor: "primary.main",
                            }}
                        >
                            <QuizOutlinedIcon />
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary">
                                Усього завдань
                            </Typography>
                            <Typography variant="h5" fontWeight={800} color="primary.main" lineHeight={1.1}>
                                {questionSummary.total}
                            </Typography>
                        </Box>
                    </Box>

                    {questionSummary.modulesWithMissingSections.length > 0 && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" fontWeight={700}>
                                Без секції: {questionSummary.totalWithoutSection}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                У цих модулях секції вже використовуються, але частині завдань секцію не призначено:
                            </Typography>
                            <Stack spacing={0.75}>
                                {questionSummary.modulesWithMissingSections.map((module) => {
                                    const missing = questionSummary.byModuleSlug.get(module.moduleSlug)?.questionsWithoutSection || [];
                                    const questionLabels = missing.map(({ index }) => `#${index + 1}`);

                                    return (
                                        <Stack
                                            key={module.moduleSlug}
                                            direction={{ xs: "column", sm: "row" }}
                                            spacing={{ xs: 0.25, sm: 1 }}
                                            alignItems={{ sm: "center" }}
                                        >
                                            <Typography variant="body2" fontWeight={700}>
                                                {module.title || module.moduleSlug}:
                                            </Typography>
                                            <Typography variant="body2" sx={{ flex: 1 }}>
                                                {questionLabels.join(", ")}
                                            </Typography>
                                            <Button size="small" color="warning" onClick={() => openEditor(module)}>
                                                Відкрити
                                            </Button>
                                        </Stack>
                                    );
                                })}
                            </Stack>
                        </Alert>
                    )}

                    <Box>
                        <TableContainer
                            component={Box}
                            sx={{
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 2,
                            }}
                        >
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700 }}>Назва</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>moduleSlug</TableCell>
                                        <TableCell sx={{ fontWeight: 700, width: 100 }} align="center">Завдань</TableCell>
                                        <TableCell sx={{ fontWeight: 700, width: 110 }}>Порядок</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>PDF</TableCell>
                                        <TableCell sx={{ fontWeight: 700, width: 120 }}>Активний</TableCell>
                                        <TableCell sx={{ fontWeight: 700, width: 230 }}>Дії</TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {loading ? (
                                        <TableRow>
                                            <TableCell colSpan={7}>
                                                <Stack direction="row" spacing={2} alignItems="center">
                                                    <CircularProgress size={18} />
                                                    <Typography variant="body2" color="text.secondary">
                                                        Завантаження...
                                                    </Typography>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ) : modules.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={7} sx={{ color: "text.secondary" }}>
                                                Список порожній
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        modules.map((m) => {
                                            const key =
                                                m.id ||
                                                `${m.classId}-${m.subjectSlug}-${m.authorSlug}-${m.moduleSlug}`;
                                            const pdfText = m.pdfPath ? `${m.pdfFileName || "PDF"}` : "—";
                                            const hasQuestions = Array.isArray(m.questions) && m.questions.length > 0;
                                            const questionCount = questionSummary.byModuleSlug.get(m.moduleSlug)?.count || 0;

                                            return (
                                                <TableRow
                                                    key={key}
                                                    sx={{ ...(!m.enabled ? { opacity: 0.6 } : {}) }}
                                                >
                                                    <TableCell sx={!m.enabled ? { textDecoration: "line-through" } : undefined}>
                                                        {m.title}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography component="span" variant="body2" sx={{ fontFamily: "monospace" }}>
                                                            {m.moduleSlug}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Chip label={questionCount} size="small" variant="outlined" />
                                                    </TableCell>
                                                    <TableCell>{m.order ?? 0}</TableCell>
                                                    <TableCell>{pdfText}</TableCell>
                                                    <TableCell>
                                                        <Switch
                                                            checked={Boolean(m.enabled)}
                                                            onChange={(e) => onToggleEnabled(m.moduleSlug, e.target.checked)}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Stack direction="row" spacing={1} alignItems="center">
                                                            <IconButton size="small" onClick={() => openEditor(m)} disabled={!m.moduleSlug}>
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>

                                                            <Tooltip
                                                                title={
                                                                    hasQuestions
                                                                        ? "Копіювати завдання в порожній модуль"
                                                                        : "У модулі немає завдань для копіювання"
                                                                }
                                                            >
                                                                <span>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => openCopyDialog(m)}
                                                                        disabled={!m.moduleSlug || !hasQuestions}
                                                                    >
                                                                        <ContentCopyIcon fontSize="small" />
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>

                                                            <Tooltip title="Embed">
                                                                <span>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => openEmbedDialog(m)}
                                                                        disabled={!m.moduleSlug || !hasQuestions}
                                                                    >
                                                                        <LinkIcon fontSize="small" />
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>

                                                            <IconButton
                                                                size="small"
                                                                onClick={() => openDeleteDialog(m)}
                                                                disabled={deletingSlug === m.moduleSlug}
                                                            >
                                                                {deletingSlug === m.moduleSlug ? (
                                                                    <CircularProgress size={18} />
                                                                ) : (
                                                                    <DeleteIcon fontSize="small" />
                                                                )}
                                                            </IconButton>

                                                            <Typography variant="caption" color="text.secondary">
                                                                {hasQuestions ? "є контент" : "порожньо"}
                                                            </Typography>
                                                        </Stack>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </CardContent>
            </Card>

            <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
                <DialogTitle>Додати модуль</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="moduleSlug"
                            value={moduleSlug}
                            onChange={(e) => setModuleSlug(e.target.value)}
                            placeholder="module001"
                            fullWidth
                        />
                        <TextField
                            label="Назва"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Наприклад: Розділ 1.1..."
                            fullWidth
                        />
                        <TextField
                            label="Порядок"
                            type="number"
                            value={order}
                            onChange={(e) => setOrder(e.target.value)}
                            fullWidth
                        />
                        <TextField
                            label="pdfPath"
                            value={pdfPath}
                            onChange={(e) => setPdfPath(e.target.value)}
                            placeholder="/pdf/ukr_avr_8/module01.pdf"
                            fullWidth
                        />
                        <TextField
                            label="pdfFileName"
                            value={pdfFileName}
                            onChange={(e) => setPdfFileName(e.target.value)}
                            placeholder="Завдання до 01.pdf"
                            fullWidth
                        />
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={closeDialog} disabled={creating}>
                        Скасувати
                    </Button>
                    <Button variant="contained" onClick={onCreate} disabled={creating}>
                        {creating ? <CircularProgress size={18} /> : "Додати"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(deleteDialogModule)} onClose={closeDeleteDialog} fullWidth maxWidth="xs">
                <DialogTitle>Видалити модуль?</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Модуль "{deleteDialogModule?.title || deleteDialogModule?.moduleSlug}" буде видалено
                            безповоротно. Щоб підтвердити дію, введіть слово "видалити".
                        </Typography>
                        <TextField
                            label='Введіть "видалити"'
                            value={deleteConfirmText}
                            onChange={(e) => setDeleteConfirmText(e.target.value)}
                            autoFocus
                            fullWidth
                            disabled={Boolean(deletingSlug)}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={closeDeleteDialog} disabled={Boolean(deletingSlug)}>
                        Скасувати
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={confirmDeleteModule}
                        disabled={Boolean(deletingSlug) || normalize(deleteConfirmText) !== "видалити"}
                    >
                        {deletingSlug === deleteDialogModule?.moduleSlug ? <CircularProgress size={18} /> : "OK"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(embedDialogModule)} onClose={closeEmbedDialog} fullWidth maxWidth="md">
                <DialogTitle>Embed: {embedDialogModule?.title || embedDialogModule?.moduleSlug}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {embedEntries.length === 0 ? (
                            <Alert severity="info">
                                У цьому модулі ще немає завдань для embed.
                            </Alert>
                        ) : (
                            embedEntries.map((entry) => (
                                <Box
                                    key={entry.key}
                                    sx={{
                                        border: "1px solid",
                                        borderColor: "divider",
                                        borderRadius: 2,
                                        p: 2,
                                    }}
                                >
                                    <Stack spacing={1.5}>
                                        <Box>
                                            <Typography variant="subtitle1" fontWeight={700}>
                                                {entry.title}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {entry.subtitle}
                                            </Typography>
                                        </Box>

                                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                                            <TextField
                                                label="Embed link"
                                                value={entry.url}
                                                size="small"
                                                fullWidth
                                                InputProps={{ readOnly: true }}
                                            />
                                            <Button
                                                variant="outlined"
                                                startIcon={<ContentCopyIcon />}
                                                onClick={() => copyEmbedText(entry.url)}
                                                sx={{ minWidth: 130 }}
                                            >
                                                Link
                                            </Button>
                                        </Stack>

                                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                                            <TextField
                                                label="Iframe code"
                                                value={entry.iframe}
                                                size="small"
                                                fullWidth
                                                multiline
                                                minRows={2}
                                                InputProps={{ readOnly: true }}
                                            />
                                            <Button
                                                variant="outlined"
                                                startIcon={<ContentCopyIcon />}
                                                onClick={() => copyEmbedText(entry.iframe)}
                                                sx={{ minWidth: 130 }}
                                            >
                                                Iframe
                                            </Button>
                                        </Stack>
                                    </Stack>
                                </Box>
                            ))
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={closeEmbedDialog}>Закрити</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={sectionsDialogOpen} onClose={() => setSectionsDialogOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>Секції модулів</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Box
                            sx={{
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 2,
                                p: 2,
                            }}
                        >
                            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                                Додати секцію
                            </Typography>
                            <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
                                <TextField
                                    label="Назва"
                                    value={newSectionTitle}
                                    onChange={(e) => setNewSectionTitle(e.target.value)}
                                    placeholder="Тестові завдання"
                                    fullWidth
                                />
                                <TextField
                                    label="Опис"
                                    value={newSectionDescription}
                                    onChange={(e) => setNewSectionDescription(e.target.value)}
                                    placeholder="Питання з варіантами відповідей"
                                    fullWidth
                                />
                                <TextField
                                    select
                                    label="Іконка"
                                    value={newSectionIconKey}
                                    onChange={(e) => setNewSectionIconKey(e.target.value)}
                                    sx={{ minWidth: { md: 190 } }}
                                >
                                    {SECTION_ICON_OPTIONS.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <TextField
                                    label="Порядок"
                                    type="number"
                                    value={newSectionOrder}
                                    onChange={(e) => setNewSectionOrder(e.target.value)}
                                    sx={{ width: { xs: "100%", md: 140 }, minWidth: { md: 140 }, flexShrink: 0 }}
                                />
                                <Button
                                    variant="contained"
                                    onClick={onCreateSection}
                                    disabled={creatingSection}
                                    sx={{ minWidth: 120 }}
                                >
                                    {creatingSection ? <CircularProgress size={18} /> : "Додати"}
                                </Button>
                            </Stack>
                        </Box>

                        <TableContainer
                            component={Box}
                            sx={{
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 2,
                            }}
                        >
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700 }}>Назва</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Опис</TableCell>
                                        <TableCell sx={{ fontWeight: 700, width: 190 }}>Іконка</TableCell>
                                        <TableCell sx={{ fontWeight: 700, width: 120 }}>Порядок</TableCell>
                                        <TableCell sx={{ fontWeight: 700, width: 120 }}>Активна</TableCell>
                                        <TableCell sx={{ fontWeight: 700, width: 170 }}>Дії</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sections.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={6} sx={{ color: "text.secondary" }}>
                                                Секцій ще немає
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        sections.map((section) => (
                                            <TableRow key={section.sectionSlug || section.slug || section.id}>
                                                <TableCell>
                                                    <TextField
                                                        size="small"
                                                        value={section.title || ""}
                                                        onChange={(e) =>
                                                            setSections((prev) =>
                                                                prev.map((item) =>
                                                                    item === section ? { ...item, title: e.target.value } : item
                                                                )
                                                            )
                                                        }
                                                        fullWidth
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        size="small"
                                                        value={section.description || ""}
                                                        onChange={(e) =>
                                                            setSections((prev) =>
                                                                prev.map((item) =>
                                                                    item === section ? { ...item, description: e.target.value } : item
                                                                )
                                                            )
                                                        }
                                                        fullWidth
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        select
                                                        size="small"
                                                        value={section.iconKey || "test"}
                                                        onChange={(e) =>
                                                            setSections((prev) =>
                                                                prev.map((item) =>
                                                                    item === section ? { ...item, iconKey: e.target.value } : item
                                                                )
                                                            )
                                                        }
                                                        fullWidth
                                                    >
                                                        {SECTION_ICON_OPTIONS.map((option) => (
                                                            <MenuItem key={option.value} value={option.value}>
                                                                {option.label}
                                                            </MenuItem>
                                                        ))}
                                                    </TextField>
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        value={section.order ?? 0}
                                                        onChange={(e) =>
                                                            setSections((prev) =>
                                                                prev.map((item) =>
                                                                    item === section ? { ...item, order: e.target.value } : item
                                                                )
                                                            )
                                                        }
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Switch
                                                        checked={section.enabled !== false}
                                                        onChange={(e) =>
                                                            onPatchSection(section, { enabled: e.target.checked })
                                                        }
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Stack direction="row" spacing={1}>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            onClick={() =>
                                                                onPatchSection(section, {
                                                                    title: normalize(section.title),
                                                                    description: normalize(section.description),
                                                                    iconKey: section.iconKey || "test",
                                                                    order: Number(section.order) || 0,
                                                                })
                                                            }
                                                            disabled={savingSectionSlug === section.sectionSlug}
                                                        >
                                                            {savingSectionSlug === section.sectionSlug ? (
                                                                <CircularProgress size={16} />
                                                            ) : (
                                                                "OK"
                                                            )}
                                                        </Button>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => onDeleteSection(section.sectionSlug)}
                                                            disabled={deletingSectionSlug === section.sectionSlug}
                                                        >
                                                            {deletingSectionSlug === section.sectionSlug ? (
                                                                <CircularProgress size={16} />
                                                            ) : (
                                                                <DeleteIcon fontSize="small" />
                                                            )}
                                                        </IconButton>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setSectionsDialogOpen(false)}>Закрити</Button>
                </DialogActions>
            </Dialog>

            <ModuleQuestionsEditorDialog
                open={editorOpen}
                onClose={closeEditor}
                initialQuestions={editingModule?.questions || []}
                initialSections={mergeSections(editingModule?.sections || [], availableSections)}
                title={editingModule?.moduleSlug ? `Контент: ${editingModule.moduleSlug}` : "Контент модуля"}
                onSave={onSaveQuestions}
            />

            <Dialog open={Boolean(copySourceModule)} onClose={closeCopyDialog} fullWidth maxWidth="sm">
                <DialogTitle>Копіювати завдання</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Alert severity="info">
                            Буде скопійовано {copySourceModule?.questions?.length || 0} завдань з модуля{" "}
                            <strong>{copySourceModule?.title || copySourceModule?.moduleSlug}</strong>.
                        </Alert>

                        <TextField
                            select
                            label="Клас"
                            value={copyTargetClassId}
                            onChange={(e) => onCopyTargetClassChange(e.target.value)}
                            fullWidth
                            disabled={copyingQuestions || copyTargetsLoading}
                        >
                            {Array.from({ length: 11 }, (_, index) => String(index + 1)).map((value) => (
                                <MenuItem key={value} value={value}>
                                    {value} клас
                                </MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            select
                            label="Предмет"
                            value={copyTargetSubjectSlug}
                            onChange={(e) => onCopyTargetSubjectChange(e.target.value)}
                            fullWidth
                            disabled={!copyTargetClassId || copyTargetsLoading || copyingQuestions}
                        >
                            {copyTargetSubjects.map((subject) => (
                                <MenuItem key={subject.subjectSlug} value={subject.subjectSlug}>
                                    {subject.title || subject.name || subject.subjectSlug}
                                </MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            select
                            label="Автор"
                            value={copyTargetAuthorSlug}
                            onChange={(e) => onCopyTargetAuthorChange(e.target.value)}
                            fullWidth
                            disabled={!copyTargetSubjectSlug || copyTargetsLoading || copyingQuestions}
                        >
                            {copyTargetAuthors.map((author) => (
                                <MenuItem key={author.authorSlug} value={author.authorSlug}>
                                    {author.name || author.title || author.authorSlug}
                                </MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            select
                            label="Порожній модуль-призначення"
                            value={copyTargetSlug}
                            onChange={(e) => setCopyTargetSlug(e.target.value)}
                            fullWidth
                            disabled={!copyTargetAuthorSlug || copyTargetsLoading || copyingQuestions}
                            helperText={
                                copyTargetAuthorSlug && !copyTargetsLoading && emptyCopyTargets.length === 0
                                    ? "У цього автора немає порожніх модулів"
                                    : "Показуються лише модулі без завдань"
                            }
                        >
                            {emptyCopyTargets.map((module) => (
                                <MenuItem key={module.moduleSlug} value={module.moduleSlug}>
                                    {module.title || module.moduleSlug} ({module.moduleSlug})
                                </MenuItem>
                            ))}
                        </TextField>

                        {copyTargetsLoading && (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <CircularProgress size={18} />
                                <Typography variant="body2" color="text.secondary">
                                    Завантаження списку...
                                </Typography>
                            </Stack>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={closeCopyDialog} disabled={copyingQuestions}>
                        Скасувати
                    </Button>
                    <Button
                        variant="contained"
                        onClick={copyQuestionsToModule}
                        disabled={!copyTargetSlug || copyingQuestions}
                        startIcon={copyingQuestions ? <CircularProgress size={16} /> : <ContentCopyIcon />}
                    >
                        Копіювати
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snack.open}
                autoHideDuration={3500}
                onClose={() => setSnack((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setSnack((s) => ({ ...s, open: false }))}
                    severity={snack.severity}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {snack.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default ModulesAdminPanel;
